import {
  customElement,
  DeesElement,
  property,
  html,
  cssManager,
  css,
  type TemplateResult,
} from '@design.estate/dees-element';

import '@design.estate/dees-catalog';
import './dotglobal-langbar.js';
import type * as interfaces from '../../ts/ffb.interfaces.js';

/**
 * Organization tile for the Explore grid and the organizations directory: avatar, name,
 * two-line bio, a language bar (with hover tooltip) and a repos/stars/site meta row.
 * Emits `dotglobal-navigate` to open the org. Reads the org-wide `languages` + `starsCount`
 * the overview attaches, so the bar and counts are correct regardless of the repo slice.
 */
@customElement('dotglobal-org-tile')
export class DotglobalOrgTile extends DeesElement {
  @property({ type: Object })
  public accessor org: interfaces.IPublicOrg | null = null;

  public static styles = [
    cssManager.defaultStyles,
    css`
      :host {
        display: block;
      }
      * {
        box-sizing: border-box;
      }
      dees-icon {
        display: inline-flex;
        flex-shrink: 0;
      }
      .row {
        display: flex;
        align-items: center;
      }
      .gap5 { gap: 5px; }
      .gap10 { gap: 10px; }
      .gap16 { gap: 16px; }
      .grow { flex: 1; }
      .stack { display: flex; flex-direction: column; }
      .nowrap { white-space: nowrap; }
      .muted { color: var(--text-muted); }

      .orgtile {
        display: flex;
        flex-direction: column;
        gap: 11px;
        min-height: 150px;
        padding: 16px;
        border: 1px solid var(--border-default);
        border-radius: 12px;
        background: var(--bg-primary);
        cursor: pointer;
        transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
      }
      .orgtile:hover {
        border-color: color-mix(in srgb, var(--accent) 50%, var(--border-strong));
        background: var(--bg-secondary);
        box-shadow: var(--shadow-sm);
      }

      .avatar {
        position: relative;
        width: 40px;
        height: 40px;
        display: inline-grid;
        place-items: center;
        flex-shrink: 0;
        overflow: hidden;
        border-radius: 8px;
        color: var(--accent-fg);
        background: var(--accent);
        font-weight: 800;
        font-size: 15px;
      }
      .avatar img {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .ot-name {
        font-family: var(--font-display);
        font-weight: 700;
        font-size: 15px;
        letter-spacing: -0.01em;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .ot-kind {
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
      .ot-bio {
        font-size: 12.5px;
        line-height: 1.5;
        color: var(--text-secondary);
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      .ot-meta {
        display: flex;
        align-items: center;
        gap: 16px;
        flex-wrap: wrap;
        font-size: 12px;
        color: var(--text-muted);
      }
      .ot-meta b {
        color: var(--text-secondary);
      }
    `,
  ];

  public render(): TemplateResult {
    const org = this.org;
    if (!org) {
      return html``;
    }
    const languages = Object.entries(org.languages ?? {})
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);
    return html`
      <div class="orgtile" @click=${() => this.navigate(`#/org/${encodeURIComponent(org.name)}`)}>
        <div class="row gap10">
          ${this.renderAvatar(org.name, org.avatarUrl)}
          <div class="stack" style="min-width: 0">
            <span class="ot-name">${org.name}</span>
            <span class="muted ot-kind">Organization</span>
          </div>
        </div>
        <div class="ot-bio">${org.description || 'Public foss.global organization.'}</div>
        <div class="grow"></div>
        <dotglobal-langbar .items=${languages} .barHeight=${6} .radius=${3}></dotglobal-langbar>
        <div class="ot-meta">
          <span class="row gap5 nowrap">${this.icon('BookOpen')} <span><b>${org.repoCount || 'public'}</b> repos</span></span>
          <span class="row gap5 nowrap">${this.icon('Star')} <b>${org.starsCount}</b></span>
          <span class="row gap5 nowrap">${this.icon('User')} <span><b>${org.memberCount}</b> people</span></span>
          ${org.website
            ? html`<span class="row gap5 nowrap" style="color: var(--accent)">${this.icon('Globe')} ${this.displayHost(org.website)}</span>`
            : ''}
        </div>
      </div>
    `;
  }

  private renderAvatar(labelArg: string, avatarUrlArg: string): TemplateResult {
    const initials = (labelArg || 'fg').split(/[.\s/-]+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'fg';
    return html`
      <span class="avatar">
        ${initials}
        ${avatarUrlArg
          ? html`<img src=${avatarUrlArg} alt="" loading="lazy" referrerpolicy="no-referrer" @error=${(eventArg: Event) => { (eventArg.currentTarget as HTMLImageElement).style.display = 'none'; }} />`
          : ''}
      </span>
    `;
  }

  private displayHost(urlArg: string): string {
    try {
      return new URL(urlArg).host;
    } catch {
      return urlArg;
    }
  }

  private icon(nameArg: string): TemplateResult {
    return html`<dees-icon .icon=${`lucide:${nameArg}`} .iconSize=${13}></dees-icon>`;
  }

  private navigate(hashArg: string): void {
    this.dispatchEvent(new CustomEvent('dotglobal-navigate', { detail: { hash: hashArg }, bubbles: true, composed: true }));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dotglobal-org-tile': DotglobalOrgTile;
  }
}
